import assertNever from "assert-never";
import { Instant } from "@js-joda/core";
import * as React from "react";
import * as Ons from "react-onsenui";
import { API, generateRequestId } from "../API";
import { ChatRoomScreen } from "../components/ChatRoomScreen";
import { isLoading } from "../Loading";
import { CaregiverId, ChatRoomId, PatientId, VisitBroadcastId } from "../messages";
import { VisitInstanceDetails, VisitInstanceDetailsWithType } from "../messages/Visit";
import { STRINGS } from "../strings";
import { AppState } from "../system/AppState";
import { Visit } from "../system/AppState/AppVisitsState";
import { AppChatState, ChatRoom } from "../system/AppState/ChatState";
import { translate } from "../translate";
import { GoToVisitParams } from "../types/goToVisitTypes";
import { getImagesDetails } from "../utils/imageUtils";
import { List } from "../utils/List";
import { memoize3 } from "../utils/memoize";
import { memoizeAll1 } from "../utils/memoizeAll";
import { reverseOrd } from "../utils/OrdTuple";
import { undefinedToNull } from "../utils/undefinedToNull";
import { ActivityStateMixin, ActivityStateMixinState } from "./ActivityStateMixin";

interface ChatActivityProps {
  api: API;
  appState: AppState;
  chatRoomId: ChatRoomId;
  onBackClick: () => void;
  // null = don't display "goToVisit" button
  goToVisit: ((params: GoToVisitParams) => void) | null;
  initialMessage?: string;
}

interface ChatActivityState {
  mixin: ActivityStateMixinState;
}

export class ChatActivity extends React.Component<ChatActivityProps, ChatActivityState> {
  constructor(props: ChatActivityProps) {
    super(props);

    this.activityStateMixin = new ActivityStateMixin(
      (state) =>
        this.setState((_prevState: ChatActivityState) => ({
          mixin: state,
        })),
      this.props.appState
    );

    this.state = {
      mixin: this.activityStateMixin.initialState(),
    };
  }

  private activityStateMixin: ActivityStateMixin;

  componentWillUnmount() {
    this.activityStateMixin.componentWillUnmount();
  }

  handleChatLastViewed = (chatRoomId: ChatRoomId, messageTime: Instant): void => {
    // TODO ... Use an intelligent "service"-type object that automatically handles errors, retries, etc...
    this.props.api.chatLastViewed(chatRoomId, messageTime);
  };

  handleUploadPhotos = async (caregiverId: CaregiverId, images: string[]) => {
    const blobs = await getImagesDetails(images);

    // upload blobs
    const s3Urls = await this.props.api.uploadPhotos(
      caregiverId,
      blobs.map((x) => x.blob)
    );

    return s3Urls.photos;
  };

  handleChatSendMessage = async (
    chatRoomId: ChatRoomId,
    message: { value: string; images: string[] }
  ): Promise<void> => {
    if (isLoading(this.props.appState.appCaregiverState)) {
      console.error("tried to send chat message while caregiver state was loading");
      return;
    }

    // TODO ... Use an intelligent "service"-type object that automatically handles errors, retries, etc...
    const requestId = generateRequestId();

    this.props.appState.chatState.addSendingMessage(chatRoomId, requestId, {
      type: "Message",
      message: message.value,
      images: message.images,
    });

    const photoUrls = await this.handleUploadPhotos(
      this.props.appState.appCaregiverState.caregiver.id,
      message.images
    );

    this.props.api.chatSend(chatRoomId, requestId, {
      value: message.value,
      images: photoUrls.length > 0 ? photoUrls : undefined,
    });
  };

  getChatRoomTitle(chatRoom: ChatRoom): string {
    let chatTitle = translate(STRINGS.LOADING) + "...";

    switch (chatRoom.chatRoomType.type) {
      case "AgencyCaregiverChat":
        if (!isLoading(this.state.mixin.appAgenciesState)) {
          const agency = this.state.mixin.appAgenciesState.agencies.get(
            chatRoom.chatRoomType.agencyId
          );
          if (agency !== undefined) {
            chatTitle = agency.agency.name + ": " + chatRoom.chatRoomType.department;
          }
        }
        break;
      case "AgencyMemberCaregiverChat":
        if (!isLoading(this.state.mixin.appAgenciesState)) {
          const agency = this.state.mixin.appAgenciesState.agencies.get(
            chatRoom.chatRoomType.agencyId
          );
          const agencyMember = this.state.mixin.appAgenciesState.allAgencyMembers.get(
            chatRoom.chatRoomType.agencyMemberId
          );
          if (agency !== undefined && agencyMember !== undefined) {
            chatTitle =
              agency.agency.name +
              " - " +
              agencyMember.details.firstName +
              " " +
              agencyMember.details.lastName;
          }

          if (agencyMember !== undefined && agencyMember.details.status !== "Active") {
            chatTitle = chatTitle.concat(` (${agencyMember.details.status} Agency Member)`);
          }
        }
        break;
      case "CaregiverVisitChat":
        if (!isLoading(this.state.mixin.appVisitsState)) {
          const visit = lookupVisit(
            this.state.mixin.appVisitsState.visits,
            chatRoom.chatRoomType.visitId
          );
          if (visit !== null) {
            chatTitle = "Visit Chat: " + visit.publicVisitBroadcastDetails.agency.name;
          }
        }
        break;
      case "CaregiverPatientChat":
        if (!isLoading(this.state.mixin.appAgenciesState)) {
          const agency = this.state.mixin.appAgenciesState.agencies.get(
            chatRoom.chatRoomType.agencyId
          );

          if (agency !== undefined) {
            chatTitle = translate(STRINGS.CHAT_WITH_AGENCY_ABOUT_PATIENT)
              .replace("%s", agency.agency.name)
              .replace("%s", chatRoom.chatRoomType.patientName);
          }
        }
        break;
      default:
        return assertNever(chatRoom.chatRoomType);
    }

    return chatTitle;
  }

  getVisitData = (): Visit | VisitInstanceDetailsWithType | null => {
    const chatRoom = AppChatState.getChatRoom(this.state.mixin.appChatState, this.props.chatRoomId);

    let visitData: ReturnType<typeof lookupVisitByPatientId> = null;

    if (chatRoom === null) {
      return null;
    }

    if (
      chatRoom.chatRoomType.type === "CaregiverVisitChat" &&
      !isLoading(this.state.mixin.appVisitsState)
    ) {
      visitData = lookupVisit(
        this.state.mixin.appVisitsState.visits,
        chatRoom.chatRoomType.visitId
      );
    }

    if (
      chatRoom.chatRoomType.type === "CaregiverPatientChat" &&
      !isLoading(this.state.mixin.appVisitsState) &&
      !isLoading(this.state.mixin.scheduledVisits)
    ) {
      visitData = lookupVisitByPatientId(
        sortVisitsCreatedAtReverse(this.state.mixin.appVisitsState.visits),
        sortScheduledVisitDetailsCreatedAtReverse(
          this.state.mixin.scheduledVisits.scheduledVisitDetails
        ),
        chatRoom.chatRoomType.patientId
      );
    }

    return visitData;
  };

  render() {
    const chatRoom = AppChatState.getChatRoom(this.state.mixin.appChatState, this.props.chatRoomId);

    if (chatRoom === null) {
      return (
        <div>
          {translate(STRINGS.LOADING)}
          <div>
            <Ons.ProgressCircular indeterminate={true} />
          </div>
        </div>
      );
    }

    const sendingMessages = this.state.mixin.appChatState.sendingChatMessages.get(chatRoom.id);

    return (
      <ChatRoomScreen
        headerTitle={this.getChatRoomTitle(chatRoom)}
        agencyMembers={this.state.mixin.appAgenciesState}
        chatRoom={chatRoom}
        visitData={this.getVisitData()}
        sendingMessages={undefinedToNull(sendingMessages)}
        getSignedPhotoUrl={(url) => this.props.api.getSignedPhotoUrl(url)}
        onBackClick={this.props.onBackClick}
        onChatMessageLastViewed={this.handleChatLastViewed}
        onSendMessage={this.handleChatSendMessage}
        goToVisit={this.props.goToVisit}
        initialMessage={this.props.initialMessage}
      />
    );
  }
}

const sortVisitsCreatedAtReverse = memoizeAll1(sortVisitsCreatedAtReverseImpl);

function sortVisitsCreatedAtReverseImpl(visits: List<Visit>): List<Visit> {
  return visits.sortBy((x) => reverseOrd(x.publicVisitBroadcastDetails.createdAt));
}

const sortScheduledVisitDetailsCreatedAtReverse = memoizeAll1(
  sortScheduledVisitDetailsCreatedAtReverseImpl
);

function sortScheduledVisitDetailsCreatedAtReverseImpl(
  scheduledVisitDetails: List<VisitInstanceDetails>
): List<VisitInstanceDetails> {
  return scheduledVisitDetails.sortBy((x) => reverseOrd(x.publicVisitDetails.createdAt));
}

function lookupVisit(visits: List<Visit>, visitId: VisitBroadcastId): Visit | null {
  for (const visit of visits) {
    if (visit.publicVisitBroadcastDetails.id === visitId) {
      return visit;
    }
  }

  return null;
}

const lookupVisitByPatientId = memoize3(lookupVisitByPatientIdImpl);

function lookupVisitByPatientIdImpl(
  visits: List<Visit>,
  visitInstances: List<VisitInstanceDetails>,
  patientId: PatientId
): Visit | VisitInstanceDetailsWithType | null {
  for (const visit of visits) {
    if (visit.publicVisitBroadcastDetails.patientId === patientId) {
      return visit;
    }
  }

  for (const visit of visitInstances) {
    if (visit.publicVisitDetails.patientId === patientId) {
      return {
        type: "VisitInstance",
        ...visit,
      };
    }
  }

  return null;
}
